import {
  Box,
  FormControl,
  Stack,
  Button,
  Select,
  MenuItem,
  useTheme,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SelectDateRange from 'src/components/SelectDateRange';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
import ModalAddAutoClipping from 'src/components/ModalAddAutoClipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useQueryUrl from 'src/hooks/useQuery';
import { useNavigate } from 'react-router';
import { STREAM_PANEL_OFFLINE_MEDIA } from 'src/route';
import { useDispatch } from 'react-redux';
import {
  reducerUpdateLanguageSelected,
  reducerUpdateMediaList
} from 'src/features/offlineMediaStream';
import TableSelectGroupPortal from 'src/components/TableSelectGroupPortal';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { IGroupPortal } from 'src/models/groupPortal';
import useGroupPortal from 'src/services/useGroupPortal';
import { toggleSelectedGroupPortalIdOfm } from 'src/features/groupPortal';
import LanguageAutocomplete from 'src/components/LanguageAutocomplete';
import { useTranslateArticle } from 'src/hooks/useTranslateArticle';
import TranslateButton from 'src/components/TranslateButton';
import {
  resetTranslation,
  setSelectedTargetLanguage
} from 'src/features/translateArticle';
import { t } from 'i18next';

const CustomBox = styled(Box)(
  ({ theme }) => `
    //width: calc(100vw - ${theme.sidebar.width} - ${theme.spacing(10)});
    margin: ${theme.spacing(2, 0)};
    //position: absolute;
`
);

const FilterBox = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing(2)};
`
);

function BottomHeader() {
  const query = useQueryUrl();
  const id = query.get('id');
  const offlineMediaType = query.get('type');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const { resTranslateArticle, isTranslated, handleClickTranslateButton } =
    useTranslateArticle();

  const { languageSelected, languages, articleList } = useAppSelector(
    (state) => state.storeOfflineMediaStream
  );
  const projectId = useAppSelector((state) => state.users.project.id);
  const { getGroupPortal } = useGroupPortal();

  const [openSelectGroup, setOpenSelectGroup] = useState(false);

  const { groupPortalList, selectedGroupPortalIdOfm } = useAppSelector(
    (state) => state.groupPortal
  );
  const { filteredList, handleFilter } = useFiltersByName<IGroupPortal>(
    groupPortalList.data
  );

  const activeArticle = useMemo(() => {
    return articleList.result.find((article) => article.originalId === id);
  }, [articleList.result, id]);

  useEffect(() => {
    getGroupPortal(projectId);
  }, [projectId]);

  useEffect(() => {
    return () => {
      dispatch(resetTranslation());
    };
  }, []);

  return (
    <CustomBox>
      <FilterBox>
        <Stack
          direction="row"
          flexWrap={'wrap-reverse'}
          alignItems="center"
          gap={{ xs: 1, md: 2 }}
          flexGrow={1}
        >
          <FormControl size="small" sx={{ maxWidth: 300 }} fullWidth>
            <Select
              name={t("group")}
              open={openSelectGroup}
              onClose={() => {
                setOpenSelectGroup(false);
                dispatch(
                  reducerUpdateMediaList(
                    filteredList
                      .filter((el) => selectedGroupPortalIdOfm.includes(el.id))
                      .flatMap((el) => el.portals)
                  )
                );
              }}
              onOpen={() => setOpenSelectGroup(true)}
              value={'always-display'}
            >
              <MenuItem value={'always-display'} sx={{ display: 'none' }}>
                {filteredList
                  .filter((el) => selectedGroupPortalIdOfm.includes(el.id))
                  .map((el) => el.displayName)
                  .join(',') || t('group')}
              </MenuItem>

              <Box sx={{ p: theme.spacing(2) }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h4">{t('group')}</Typography>
                </Stack>
                <Box sx={{ mt: theme.spacing(1) }}>
                  <TableSelectGroupPortal
                    groupPortals={filteredList}
                    selectedGroupByIds={selectedGroupPortalIdOfm}
                    loading={groupPortalList.loading}
                    onClick={(group) => {
                      dispatch(toggleSelectedGroupPortalIdOfm(group.id));
                    }}
                  />
                </Box>
              </Box>
            </Select>
          </FormControl>
          <ModalAddAutoClipping clippingType="offlineMedia" />
        </Stack>
        <Stack spacing={2} direction="row">
          {activeArticle && (
            <TranslateButton
              handleTranslate={(selectedLanguage: string) => {
                dispatch(setSelectedTargetLanguage(selectedLanguage));
                handleClickTranslateButton({
                  title: activeArticle.title,
                  content: activeArticle.content,
                  language: selectedLanguage ? selectedLanguage : undefined
                });
              }}
              loading={resTranslateArticle.loading}
              isTranslated={isTranslated}
            />
          )}
          <LanguageAutocomplete
            languages={languages}
            languageSelected={languageSelected}
            onChange={(newValue) =>
              dispatch(
                reducerUpdateLanguageSelected(
                  newValue?.language || 'all-language'
                )
              )
            }
          />
          <FormControl size="small" sx={{ width: 130, minWidth: 130 }}>
            <SelectDateRange />
          </FormControl>
        </Stack>
      </FilterBox>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ mt: id ? 2 : 1 }}
      >
        {id && (
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 1
            }}
            onClick={() =>
              navigate(
                `${STREAM_PANEL_OFFLINE_MEDIA}${offlineMediaType ? `?type=${offlineMediaType}` : ''
                }`
              )
            }
          >
            {t('media profile preview')}
          </Button>
        )}
      </Stack>
    </CustomBox>
  );
}

export default BottomHeader;
